import { ProductType } from 'app/dp-world/services/product-config.service';
import { CreditLimitDetails } from '../credit-limit/credit-limit.model';

export enum FinanceSubClassification {
  pre = 'pre',
  post = 'post',
}

export interface Finance {
  financeSubClassification: FinanceSubClassification;         // "pre",
  createdBy: string;                // "ac0f370623415f8be75c2dac39b3cf6ce6933660c4e8510d16a907cccd3102b1",
  sellerId: string;               // "org_id_1",
  exporterId: string;               // "org_id_2",
  incoTerms: string;                // "EXW",
  amount: string;                   // "1234.56789",
  currency: string;                 // "USD",
  shipmentOriginPort: string;       // "Dubai",
  shipmentDestinationPort: string;  // "Mumbai"
  currentStep: ApplySteps;          // The current step the user is on.
}

// new version of Finance, used to save and submit to backend.
export interface Finance2 {
  'financeSubClassification': string;     // "pre",
  'id'?: string;
  'createdBy': string;                    // "ac0f370623415f8be75c2dac39b3cf6ce6933660c4e8510d16a907cccd3102b1",
  'sellerId': string;                   // "org_id_1",
  'exporterId': string;                   // "org_id_2",
  'incoTerms': string;                    // "EXW",
  'shipmentOriginPort': string;           // "Dubai",
  'shipmentDestinationPort': string;      // "Mumbai",
  'invoiceAmount': number;                // 1234.56789,
  'requestedAmount': number;              // 1200,
  'financeCurrency': string;              // "USD",
  'cargoDetails': [
    {
      'cargoDescription': string;      // "details about cargo",
      'quantity': number;              // 3,
      'unitPrice': number;             // 1.50
    }
  ];
  'requestedBanks': [
    {
      'id': string;                   // "1000000",
      'accountNumber': string;        // "acc_num01"
    }
  ];
  'disbursalEvents': [
    {
      'paymentPercentage': number;     // 20,
      'paymentEvent': string;          // "EXPORTER_PRODUCTION_COMPLETED",
      'daysAfterDisbursalEvent': number; // 10
      'interestBearer': string;        // "IMPORTER",
      'recourse': string;              // "EXPORTER"
    }
  ];
  'settlementEvents': [
    {
      'settlementPercentage': number;     // 20,
      'settlementEvent': string;          // "EXPORTER_PRODUCTION_COMPLETED",
      'daysAfterSettlementEvent': number; // 10
    }
  ];
}

export enum ApplySteps {
  Start = 'Start',
  Apply = 'Apply',
  Endorse = 'Endorse',
  Enrich = 'Enrich',
  Assessment = 'Assessment',
  AcceptTerms = 'AcceptTerms',
  Approved = 'Approved',
  CargoLoaded = 'CargoLoaded',
  FundsTransfered = 'FundsTransferred',
  AccountSettled = 'AccountSettled',
}

export enum EnvironmentType {
  Dev = 'Dev',
  Staging = 'Staging',
  Prod = 'Prod',
  qa = 'qa',
  development = 'development',
  uat = 'uat'
}

export enum FinanceStates {
  // WHEN I RECIEVE     ->      // WHAT I SHOW
  BCO_FINANCE_SAVED = 'BCO_FINANCE_SAVED',
  DTF_FINANCE_ACCEPTED = 'DTF_FINANCE_ACCEPTED',
  BANKS_FINANCE_ASSESSED = 'BANKS_FINANCE_ASSESSED',
  BCO_TERMS_AGREED = 'BCO_TERMS_AGREED',

  BANK_FUNDS_TRANSFERRED = 'BANK_FUNDS_TRANSFERRED',      // Funds Successfully Transferred
  FUNDS_RETURNED_UPDATED_BY_MAKER = 'FUNDS_RETURNED_UPDATED_BY_MAKER',
  FUNDS_RETURNED_UPDATED_BY_CHECKER = 'FUNDS_RETURNED_UPDATED_BY_CHECKER',
  FUNDS_RETURNED = 'FUNDS_RETURNED',
  ACCOUNT_SETTLED = 'ACCOUNT_SETTLED',                    // Account Settled
  DTF_FINANCE_CLOSED = 'DTF_FINANCE_CLOSED',              // Account Settled
  FINANCE_CANCELLED = 'FINANCE_CANCELLED',                 // Account Settled
  DETAILS_REQUIRED = 'DETAILS_REQUIRED',
  IMPORTER_APPROVAL_PENDING = 'IMPORTER_APPROVAL_PENDING',
  APPROVED_BY_MAKER = 'APPROVED_BY_MAKER',
  REJECT_IN_PROGRESS = 'REJECT_IN_PROGRESS',
  REWORK_PENDING_BY_MAKER = 'REWORK_PENDING_BY_MAKER',
  FINANCE_ASSESSMENT_REQUESTED_FOR_DEFAULTER = 'FINANCE_ASSESSMENT_REQUESTED_FOR_DEFAULTER',
  REJECTED = 'REJECTED',
  REJECTED_BY_APPLICANT = 'REJECTED_BY_APPLICANT',
  OVERDUE_WAIVE_OFF_REQUESTED = 'OVERDUE_WAIVE_OFF_REQUESTED',
  PARTIALLY_SETTLED = 'PARTIALLY_SETTLED',
  WAIVER_REQUEST = 'WAIVER_REQUEST',
  WAIVER_REQUEST_APPROVED = 'WAIVER_REQUEST_APPROVED',
  WAIVER_REQUEST_REJECTED = 'WAIVER_REQUEST_REJECTED', // Credit Team to provide approval before disbursal
  REJECTED_BY_FI = 'REJECTED_BY_FI',
  DUE_DATE_UPDATE_REQUESTED = 'DUE_DATE_UPDATE_REQUESTED',
  FUNDS_TRANSFER_UPDATED_BY_MAKER = 'FUNDS_TRANSFER_UPDATED_BY_MAKER',
  FUNDS_TRANSFER_PENDING_REWORK = 'FUNDS_TRANSFER_PENDING_REWORK',
  SETTLEMENT_UPDATED_BY_MAKER = 'SETTLEMENT_UPDATED_BY_MAKER',
  PARTIAL_SETTLEMENT_UPDATED_BY_MAKER = 'PARTIAL_SETTLEMENT_UPDATED_BY_MAKER',
  PARTIAL_SETTLEMENT_PENDING_REWORK = 'PARTIAL_SETTLEMENT_PENDING_REWORK',
  SETTLEMENT_PENDING_REWORK = 'SETTLEMENT_PENDING_REWORK',
  BALANCE_SETTLEMENT_UPDATED_BY_MAKER = 'BALANCE_SETTLEMENT_UPDATED_BY_MAKER',
  BALANCE_SETTLEMENT_PENDING_REWORK = 'BALANCE_SETTLEMENT_PENDING_REWORK',
  DTF_FINANCE_SETTLED = 'DTF_FINANCE_SETTLED',
  INTEREST_SCHEDULE_UPDATE_REQUESTED = 'INTEREST_SCHEDULE_UPDATE_REQUESTED',
  INTEREST_SCHEDULE_REWORK_PENDING = 'INTEREST_SCHEDULE_REWORK_PENDING',
  CM_REVIEW_PENDING_OM_CHECKER = 'CM_REVIEW_PENDING_OM_CHECKER',
  CM_REWORK_PENDING_OM_MAKER = 'CM_REWORK_PENDING_OM_MAKER',
  CM_REVIEW_PENDING_FM = 'CM_REVIEW_PENDING_FM',
  CM_RESUBMISSION_PENDING_FM_CHECKER = 'CM_RESUBMISSION_PENDING_FM_CHECKER',
  CM_REWORK_PENDING_OM_CHECKER = 'CM_REWORK_PENDING_OM_CHECKER',
  CM_AWAIT_ACCOUNTING_APPROVE = 'CM_AWAIT_ACCOUNTING_APPROVE',
  CM_REJECTED_BY_OM_CHECKER = 'CM_REJECTED_BY_OM_CHECKER',
  CM_REJECTED_BY_FM = 'CM_REJECTED_BY_FM',
  CM_ACCOUNTING_APPROVED = 'CM_ACCOUNTING_APPROVED',
  CONVERSION_REQUEST_PENDING_CLIENT_SUBMISSION = 'CONVERSION_REQUEST_PENDING_CLIENT_SUBMISSION',
  CONVERSION_REQUEST_PENDING_MAKER_REVIEW = 'CONVERSION_REQUEST_PENDING_MAKER_REVIEW',
  CONVERSION_REQUEST_PENDING_CHECKER_REVIEW = 'CONVERSION_REQUEST_PENDING_CHECKER_REVIEW',
  CONVERSION_REQUEST_PENDING_CLIENT_RESUBMISSION = 'CONVERSION_REQUEST_PENDING_CLIENT_RESUBMISSION',
  CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION = 'CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION',
  CONVERSION_REQUEST_REJECTED_BY_MAKER = 'CONVERSION_REQUEST_REJECTED_BY_MAKER',
  CONVERSION_REQUEST_REJECTED_BY_CHECKER = 'CONVERSION_REQUEST_REJECTED_BY_CHECKER',
  CONVERTED_TO_POST_SHIPMENT = 'CONVERTED_TO_POST_SHIPMENT',
  POST_SHIPMENT_CONVERSION_IN_PROCESS = 'POST_SHIPMENT_CONVERSION_IN_PROCESS'
}


// used by save and submit
export interface FinanceReply {
  data: {
    financeId: number; // 1234
  };
}

export interface SubmitFinanceResponse {

}

export interface UserDetails {
  id: string;                    // "fe8559dd3683b1ebf2413724d8d9b6cfb1e51aa3",
  user_full_name: string;              // "Joey Tribbiani",
  organisation_full_name: string;     // "Digital Trade Finance",
  approval_status: string;            // "pending",
  created_at: string;                 // "2020-04-14T09:17:35.744Z",
  updated_at: string;                 // null,
  deleted_at: string;                 // null,
  user_organisation_type: string;     // "DTF",
  organisation_id: string | number;            // 1000001
  mawani_org_reference_id: string;
  country: string;
}

export interface CargoDetail {
  id: number;                               // 1000005,
  cargo_description: string;                // "details about cargo",
  quantity: number;                         // 3,
  unit_price: string;                       // "1.500000"
}

export interface BankDetail {
  fi_id: number;                          // 1000011,
  bank_account_number: string;              // null,
  approval_status: string;                  // "pending"
  bank_full_name: string;
  is_lending_organisation?: boolean;
  fees_from_integrated_fi?: boolean; // get existing data from bhoomi, for lending this flag will always be false
}

export interface DisbursalEvent {
  payment_term_id: number;          // 1000006,
  payment_percentage: string;       // "20.00",
  disburse_on_event: string;        // "EXPORTER_PRODUCTION_COMPLETED",
  interest_bearer: string;          // "IMPORTER",
  payment_deadline_in_days: number; // 10
  recourse: string;                 // "EXPORTER",
  actual_disbursal_date: string;    // null,
  created_at: string;               // "2020-03-30T03:35:10.171Z",
  updated_at: string;               // null,
  deleted_at: string;               // null
  actual_payment_date: string;
  id: number;
}

export interface SettlementEvent {
  payment_term_id: number;          // 1000006,
  settlement_percentage: string;    // "20.00",
  settle_on_event: string;          // "EXPORTER_PRODUCTION_COMPLETED",
  actual_settlement_date: string;   // null,
  settlement_deadline_in_days: number;
  created_at: string;               // "2020-03-30T03:35:10.171Z",
  updated_at: string;               // null,
  deleted_at: string;               // null,
  id: number;
}

export interface FinanceDocumentDetail {
  id: string;                       // 1000008,
  document_name: string;            // "jXF7PcmLCVCCv98Xrecuct7Z.png",
  document_type: string;            // "proforma_invoice",
  document_location: string;        // "https://dtlp-file-storage-development.s3.ap-south-1.amazonaws.com/trade-finance/finances/1000003/jXF7PcmLCVCCv98Xrecuct7Z.png",
  uploaded_by: string;              // "Griffie Dillingston",
  accessibleUsers: [
    '258531e6b6bb130080db67666161967207035ac3cf43d10df0de71ece10c2665'
  ];
  created_at: Date;
  documentName?: string;
  ownerType?: string;
  source?: string;
}

export interface IntegratedFees {
  fee_name: string,
  fee_value: number
}

export interface UserDisbursalEvent {
  created_at: string;                 // "2020-05-22T18:15:05.838Z",
  deleted_at: string;                 // null,
  disburse_on_event: string;          // "EXPORTER_PRODUCTION_COMPLETED",
  finance_id: number;                 // 1000034,
  id: number;                         // 1000059,
  interest_bearer: string;            // "IMPORTER",
  payment_percentage: string;         // "80.00",
  payment_deadline_in_days: number;   // 10
  recourse: string;                   // "EXPORTER",
  updated_at: string;                 // null,

  // Logistics
  discount_rate: string;
}

export interface UserSettlementEvent {
  settle_on_event: string;            // "EXPORTER_PRODUCTION_COMPLETED",
  finance_id: number;                 // 1000034,
  id: number;                         // 1000059,
  settlement_percentage: string;      // "80.00",
  settlement_deadline_in_days: number;   // 10
  created_at: string;                 // "2020-05-22T18:15:05.838Z",
  deleted_at: string;                 // null,
  updated_at: string;                 // null,
}

export interface TransactionDetails {
  id: number;
  created_at: string;
  deleted_at: string;
  updated_at: string;
  created_by: string;
  credit_account: string;
  credit_to_org: number;
  debit_account: string;
  debit_from_org: number;
  document_upload_id: string;
  dpwfs_finance_commission: string;
  dpwfs_miscellaneous_fees_commission: string;
  external_transaction_id: string;
  finance_id: number;
  mode_of_payment: string;
  notes: string;
  other_charges_amount: number;
  other_charges_type: string;
  tenant_code: string;
  transaction_amount: string;
  transaction_currency: string;
  transaction_event: string;
  transaction_event_date: string;
  transaction_event_type: string;
  transaction_type: string;
}

export interface StateTransactionsDetail {
  user_id: string;                  // "258531e6b6bb130080db67666161967207035ac3cf43d10df0de71ece10c2665",
  user_full_name: string;           // "Griffie Dillingston",
  state: string;                    // "BCO_FINANCE_SAVED",
  notes: string;                    // "finance raised",
  created_at: string;               // "2020-03-30T03:34:53.924Z",
  updated_at: string;               // null,
  deleted_at: string;               // null
}

export interface PaymentTerms {
  actual_discount_fee_amt: string;
  actual_processing_fee_amt: string;
  actual_finance_tenure: number;
  discount_rate_type: any;
  discount_rate: any;
  discount_rate_per_month: any;
  actual_payment_date: any;
  payment_date: any;
  id: number;                               // 1000006,
  finance_id: number;                       // 1000003,
  bank_id: number;                          // 1000012,
  sanctioned_amount: string;                // null,
  sanctioned_currency: string;              // null,
  interest_rate: string;                    // null,
  payment_deadline: string;                 // null,
  processing_fee: string;                   // null,

  buyer_terms_review_required: boolean;
  fi_bank_id: number;

  counter_party_acceptance: string;
  applicant_acceptance: string;
  applicant_bank_id: number;
  counter_party_bank_id: string;
  sponsor_bank_id?: string;
  created_at: string;                       // "2020-03-30T03:35:10.171Z",
  updated_at: string;                       // null,
  deleted_at: string;
  receivable_amount?: string;
  requested_amount?: string;                    // null,
  disbursalEvents: Array<DisbursalEvent>;
  settlementEvents: Array<SettlementEvent>;
  payment_date_by_bank: Date;
  fin_inst_bank_account: string;
  seller_bank_account: string;
  sanction_amt_percentage: any;
  payment_event_name: string;
  payment_event_by_bank_name: string;
  approved_finance_tenure: number;
  requested_finance_tenure: number;
  requested_sanction_amt_percentage: number;
  requested_discount_fee_amt: number;
  approved_discount_fee_amt: number;
  requested_processing_fee_amt: number;
  approved_processing_fee_amt: number;
  processing_fee_type: string;
  finance_commission_amt: number;
  transaction_fee_amt: number;
  misc_fees_commission_amt: number;
  settlement_amount?: number;
  bl_date?: Date;
  finance_rate_source?: string;
  fees_actual?: FeesActual;
  tenant_code?: string;
  factoringFee?: number;
  // factoringFeeAmount?: number;
  factoringFeeType?: string;
  factoringFeeAmountApproved?: number;
  overRideInterestRateValue?: number;
  approved_finance_rate_type: string;
  requested_finance_rate_type: string;
  approved_margin: string;
  requested_margin: string;
  requestedFactoringFeeAmt?: number,
  approvedFactoringFeeAmt?: number;
}

export enum QuerySortingAndFilterEnum {
  _ORDER = '_order',
  _SORT = '_sort',
  _FILTER = 'filter',
}
export interface QueryParamsInterface {
  _order?: string;
  _sort?: string;
  _filter?: string;
}
/**
 * When we load an existing finance by id on apply tab.
 */
export interface FullFinance {
  sponsor_name?: string,
  internalCounterPartyDetails: any;
  counterPartyDetails: any[];
  discount_rate_per_month: any;
  processing_fee: any;
  actual_payment_date: any;
  payment_date: any;
  assigned_to: string;                              // looks like a GUID //"fe8559dd3683b1ebf2413724d8d9b6cfb1e51aa3" DPW admin this finance is assigned to
  collection_amount: number;                        // usually null, what is this for?
  // financeId: string;                              // old DEPRECATED "1000003",
  finance_id: string;
  fi_name: string; /* finance institute name */
  // finance_sub_classification: string;               // "pre",
  sofrDetails?: any;

  financeType?: ProductType;
  finance_type: string;                         // was added as a plugin after getting back from backend.
  finance_scheme: string;
  exporterDetail: any;
  importerDetail: any;
  logistics_finance_type: string;
  finance_product_type: string;
  productType?: ProductType,
  applicant_id: number,
  counter_party_id: number,
  fi_id?: number,
  product_type?: string,
  product_category?: string,
  applicant_name: string,
  counter_party_name: string,
  applicant_acceptance?: string,

  credit_limit_id: number;
  approved_due_date: string;
  created_by: string;                               // "258531e6b6bb130080db67666161967207035ac3cf43d10df0de71ece10c2665",
  state: FinanceStates;                             // "",
  proof_type: string;                               // "purchase_order",
  endorse_proof_type: string;                       // "purchase_order",
  inco_terms: string;                               // "EXW",
  submitted_at: string;                             // "2020-03-30T03:35:10.050Z",
  shipment_origin_port: string;                     // "Dubai",
  shipment_destination_port: string;                // "Mumbai",
  shipment_origin_city: string;
  shipment_destination_city: string;
  shipment_origin_country: string;
  shipment_destination_country: string;
  shipment_description: null;
  invoice_amount: string | number;                           // "1212.444568",
  disbursementCurrency: string;                         // "USD",
  invoiceCurrency: string;
  additionalSupportedInvoiceCurrencies: any[],
  requested_amount: string;                         // "800.000000",
  requested_currency: string;                       // "USD",
  collection_currency: null;
  fees_from_integrated_fi: boolean;
  integratedFees?: Array<IntegratedFees>;
  created_at: string;                               // "2020-03-30T03:34:53.924Z",
  updated_at: string;                               // "2020-03-30T03:35:10.171Z",
  deleted_at: string;
  banksDetails: Array<BankDetail>;
  cargoDetails: Array<CargoDetail>;
  documentsDetails: Array<FinanceDocumentDetail>;
  paymentTermsDetails: Array<PaymentTerms>;
  stateTransactionsDetails: Array<StateTransactionsDetail>;
  transactionsDetails: TransactionDetails[];
  userDisbursalEventsDetails: Array<UserDisbursalEvent>;
  userSettlementEventsDetails: Array<UserSettlementEvent>;
  usersDetails: Array<UserDetails>;
  transactionMonitoringDeails: any[];
  fiTermValidationDeails: any[];
  organisaitonBankDetails: any[];
  receivable_amount: string;
  invoice_id: string;
  invoice_date: string;
  due_date: string;
  isOverdueChargeable: boolean;
  remarks: string;
  remarksDetails: Array<RemarksDetails>;
  lendingRemarks?: any[];
  invoicesDetails: Array<any>;
  receivable_currency: string;
  creditLimitDetails: CreditLimitDetails;
  dpwCommissionDetails: DPWCommisionDetails[];
  requested_sanction_amt_percentage: number;
  documentDetails: [];
  invoiceStalePeriod?: number;
  id: string;
  mblNumber: string;
  containerNumber: string;
  carrierName: string;
  bookingNumber: string;
  shipmentReference: string;
  tracking_url: string;
  shipment_status: string;
  trackingInformation: ITrackingInformation[];
  is_lending_organisation?: boolean;
  userRolesInfo?: UserRoleInterface;
  active_user?: string;
  approved_credit_charges_batch_id?: string;
  updated_approved_due_date?: any;
  lendingDocuments?: any[];
  adviceDocuments?: any[];
  ocrPredictedData: any;
  annualProcessingFeeCollected: boolean;
  facilityEnhancementFeeCollected: boolean;
  facilityRenewalFeeCollected: boolean;
  overdueFeeDetails: any;
  tenant_code: any;
  ehblDetails?: any[];
  request_update_due_date?: string;
  reason_for_update_due_date?: string;
  taxDetails?: TaxDetails;
  active_workflow?: string;
  authorisedSignApprovalPendingArr?: any[];
  authorisedSingatoryApprovalPending?: boolean;
  delta_field?: string;
  marginAmountDetails?: BankReceiptInterface[];
  totalAmountPayableToSupplier?: any;
  is_exhausted?: boolean;
  firstDisbursalApplicationInYearForCreditLimit?: any;
  firstDisbursalApplicationAfterEnhancement?: any;
  firstDisbursalApplicationAfterRenewal?: any;
  requestedTerms?: requestedTerms[];
  changeApprovalActionForDefaultedClients?: boolean;
  feeChargesCollectionDetails: {
    transactionFeeCollectionAt: string,
    interestCollectionAt: string,
    factoringFeeCollectionAt?: string,
  };
  custom_interest_schedule?: any;
  custom_interest_schedule_details?: any;
  requested_custom_interest_data?: any;
  multi_currency_details: any;
  purchaseOrderDetails?: any;
  masterInstrumentDetails?: any;
  preshipmentApplicationDetails?: any;
  flags?: any[];
}

export interface BankReceiptInterface {
  bankReferenceNumber: any;
  ownerId?: any;
  ownerType?: any;
  receiptDate: any;
  receiptAmountReceived?: any;
  receiptBalanceAmount?: any;
  receiptAmountRemitted?: any;
  receiptBalanceLeft?: any;
  receiptCategory?: any;
  receiptCategoryId?: any;
  receiptCategoryStatus?: any;
  delta_field?: string;
  sofrDetails?: any;
}

export interface requestedTerms {
  inventoryItemName: string,
  inventoryQuantity: number,
  inventoryItemValue: number,
  inventoryTotalValue: number,
  inventoryGuid: string
}

export interface TaxDetails {
  tax_amount?: any;
  tax_regime_code?: any;
  tax_rate_code?: any;
  balance_tax_amount?: number;
  balance_factoring_fee_tax_amount?: number
}

export interface UserRoleInterface {
  sectionAllowedInfo: SectionParamInterface;
  actionAllowedInfo: string[];
}

export interface SectionParamInterface {
  invoiceDetails?: string;
  supportingDocuments?: string;
  approvedTerms?: string;
  customInterestRate?: string;
  dpwfsCommision?: string;
  trackingInformation?: string;
  flags?: any[];
  fiApprovalDocs?: string;
  assignmentOfReceivables?: string;
  remarks?: string;
  internalComments?: string;
}

export enum CommonFinanceStates {
  BCO_FINANCE_SAVED = 'Draft_Provide the requisite details and submit the finance application for Financial Institution assessment.',        // Trade Finance Apply step
  DTF_FINANCE_ACCEPTED = 'Under Assessment_Application is under the financial institution assessment.',    // Document Endorse step (other BCO reviewing)
  BANK_FUNDS_TRANSFERRED = 'Funds Transferred_Congratulations! The amount is being transferred.',      // Funds Successfully Transferred
  BCO_TERMS_AGREED = 'Terms Accepted_All parties have accepted the terms. Funds to be transferred.',                  // Trade Finance Approved
  ACCOUNT_SETTLED = 'Account settled _The financial institution has received back the amount and the account has been settled.',                    // Account Settled
  ACCOUNT_SETTLED_BY_FI = ACCOUNT_SETTLED,
  DTF_FINANCE_CLOSED = 'Application Closed_This finance is 100% complete and has been closed.',              // Account Settled
  FINANCE_CANCELLED = 'Cancelled_This finance was cancelled.',                 // Account Settled
  DETAILS_REQUIRED = 'Financial Institution has requested for more information',
  APPROVED_BY_MAKER = 'Under Assessment_Application is under the financial institution assessment.',
  REWORK_PENDING_BY_MAKER = 'Rework is pending by maker',
  FINANCE_ASSESSMENT_REQUESTED_FOR_DEFAULTER = 'Finance Assessment requested for defaulter',
  PARTIALLY_SETTLED = 'Partially settled',
  WAIVER_REQUEST = 'Waiver request',
  WAIVER_REQUEST_REJECTED = 'Waiver Request Rejected',
  REJECTED_BY_FI = 'Finance Rejected _The application has been rejected by Financial Institution.',
  DUE_DATE_UPDATE_REQUESTED = 'Due date update requested _Credit team approval requested for update in due date.',
  FUNDS_TRANSFER_UPDATED_BY_MAKER = 'Funds Transfer Updated by Maker',
  FUNDS_TRANSFER_PENDING_REWORK = 'Funds Transfer Pending Rework',
  PARTIAL_SETTLEMENT_PENDING_REWORK = 'Partial Settlement Pending Rework',
  PARTIAL_SETTLEMENT_UPDATED_BY_MAKER = 'Partial settlement updated by maker.',
  SETTLEMENT_UPDATED_BY_MAKER = 'Settlement Updated by Maker',
  SETTLEMENT_PENDING_REWORK = 'Settlement Pending Rework',
  BALANCE_SETTLEMENT_UPDATED_BY_MAKER = 'Balance Settlement Updated by Maker',
  BALANCE_SETTLEMENT_PENDING_REWORK = 'Balance Settlement Pending Rework',
  OVERDUE_WAIVE_OFF_REQUESTED = 'Overdue waive off Requested _The maker or checker has requested to waive off the overdue amount.',
  DTF_FINANCE_SETTLED = 'DTF Finance Settled',
  IMPORTER_APPROVAL_PENDING = 'Financing terms review _The financial institution has accepted the application. Importer has to accept the terms for the amount to be sanctioned.',        // Document Endorse step (other BCO reviewing)',
  BANKS_FINANCE_ASSESSED = 'Financial Institution Assessed_The financial institution has accepted the application. <PARTIES_INVOLVED> have to accept the terms for the amount to be sanctioned.',
  FUNDS_RETURNED_UPDATED_BY_MAKER = 'Funds returned updated by maker',
  FUNDS_RETURNED_UPDATED_BY_CHECKER = 'Funds returned updated by checker',
  FUNDS_RETURNED = 'Funds returned _This finance is 100% complete and has been closed.',
  INTEREST_SCHEDULE_UPDATE_REQUESTED = 'Interest Schedule Updated by Maker',
  INTEREST_SCHEDULE_REWORK_PENDING = 'Interest Schedule Rework pending with Maker',
  CM_REVIEW_PENDING_OM_CHECKER = 'Credit Note Review Pending with Operations Checker',
  CM_REWORK_PENDING_OM_MAKER = 'Credit Note Rework Pending with Operations Maker',
  CM_REVIEW_PENDING_FM = 'Credit Note Review Pending with Finance Manager',
  CM_RESUBMISSION_PENDING_FM_CHECKER = 'Credit Note Accounting Resubmission Pending with Finance Checker',
  CM_REWORK_PENDING_OM_CHECKER = 'Credit Note Rework Pending with Operations Checker',
  CM_AWAIT_ACCOUNTING_APPROVE = 'Credit Note Awaiting Accounting Approval',
  CONVERSION_REQUEST_PENDING_CLIENT_SUBMISSION = 'Conversion Request Pending_Submit the conversion request to proceed.',
  CONVERSION_REQUEST_PENDING_MAKER_REVIEW = 'Conversion Request Under Review_Maker review is in progress.',
  CONVERSION_REQUEST_PENDING_CHECKER_REVIEW = 'Conversion Request Under Review_Checker review is in progress.',
  CONVERSION_REQUEST_PENDING_CLIENT_RESUBMISSION = 'Conversion Request Resubmission Pending_Client resubmission is required.',
  CONVERSION_REQUEST_PENDING_MAKER_RESUBMISSION = 'Conversion Request Resubmission Pending_Maker resubmission is required.',
  CONVERSION_REQUEST_REJECTED_BY_MAKER = 'Conversion Request Rejected_Rejected by maker.',
  CONVERSION_REQUEST_REJECTED_BY_CHECKER = 'Conversion Request Rejected_Rejected by checker.',
  POST_SHIPMENT_CONVERSION_IN_PROCESS = 'Post-Shipment Conversion In Process_Conversion is currently in progress.',
  CONVERTED_TO_POST_SHIPMENT = 'Converted to Post-Shipment_The finance has been successfully converted to post-shipment.'
}

// export enum FinanceStatesApplication {
//   // WHEN I RECIEVE     ->      // WHAT I SHOW
//   BCO_FINANCE_SAVED = 'Trade finance application _Provide the requisite details to start the finance application.',        // Trade Finance Apply step
//   DTF_FINANCE_ACCEPTED = 'Financial institution assessment _Once the documents are endorsed, the financial institution will assess the application. You will have to accept the terms of the financial institution thereafter.',             // Bank Assessment (Banker review)
//   BANKS_FINANCE_ASSESSED = 'Financing terms review _The financial institution has accepted the application. Both parties have to accept the terms for the amount to be sanctioned.',      // Accept Terms (BCO's review terms)
//   BCO_TERMS_AGREED = 'Funds to be transferred _All parties have accepted the terms. Funds to be transferred.',                  // Trade Finance Approved
//   BANK_FUNDS_TRANSFERRED = 'Funds transferred _Congratulations! The amount is being transferred.',      // Funds Successfully Transferred
//   ACCOUNT_SETTLED = 'Account settled _The financial institution has received back the amount and the account has been settled.',                    // Account Settled
//   ACCOUNT_SETTLED_BY_FI = ACCOUNT_SETTLED,
//   DTF_FINANCE_CLOSED = 'Finance Closed _This finance is 100% complete and has been closed.',              // Account Settled
//   FINANCE_CANCELLED = 'Finance Cancelled _This finance was cancelled.',                 // Account Settled
//   OVERDUE_WAIVE_OFF_REQUESTED = 'Overdue waive off Requested _The maker or checker has requested to waive off the overdue amount.',
//   PARTIALLY_SETTLED = 'Partially settled',
//   WAIVER_REQUEST = 'Waiver request',
//   WAIVER_REQUEST_REJECTED = 'Waiver Request Rejected',
//   REJECTED_BY_FI = 'Finance Rejected _The application has been rejected by Financial Institution.',
//   DUE_DATE_UPDATE_REQUESTED = 'Due date update requested _Credit team approval requested for update in due date.',
//   DTF_FINANCE_SETTLED = 'DTF Finance Settled'
// }

/** The documents signed via Docusign */
export enum DocusignDocuments {
  PurchaseAgreement = 'purchase_agreement',
  DiscountRequest = 'discount_request',
  AssignmentToDT = 'assignment_agreement_payment_to_DT',
  AssignmentSellerAsAgent = 'assignment_agreement_seller_as_agent',
  NoticeToDT = 'notice_of_assignment_payment_to_DT',
  NoticeSellerAsAgent = 'notice_of_agreement_seller_as_agent',
}


export interface SharedData {
  countryPortsAndCities: Array<CountryPortsAndCities>;
  supportedBanks: Array<Bank>;
  supportedCurrencies: string[];
  supportedDocumentOwnerTypes: object;
  supportedDocumentTypeDetails: any[];
  supportedExporterShipmentEvents: string[];
  supportedFileExtensions: string[];
  supportedImporterShipmentEvents: string[];
  supportedIncoTerms: string[];
  supportedIncoTermsDetails: Array<IncoTermsDetails>;
  supportedOwnerDocumentTypes: any[];
  supportedProofTypes: string[];
  supportedShipmentEvents: string[];
  commodity: string[];
  countryList: Array<CountryAndCode>
}

export interface CountryPortsAndCities {
  cities: string[];
  country_name: string;
  ports: string[];
}

export interface CountryAndCode {
  country_code: string;
  country_name: string;
}

export interface Bank {
  approval_notes: string;
  approved_by: string;
  country: string;
  created_at: Date;
  deleted_at: Date;
  full_name: string;
  id: number;
  invited_by: string;
  is_verified: boolean;
  mawani_org_reference_id: string;
  organisation_address_primary: string;
  organisation_contact_email_primary: string;
  organisation_contact_phone_primary: string;
  organisation_type: string;
  updated_at: Date;
}


export interface IncoTermsDetails {
  description: string;
  value: string;
}

export interface RemarksDetails {
  user_id: string;
  companyName: string;
  finance_id: number;
  finance_type: string;
  message: string;
  user_name: string;
  created_at: Date;
  documentDetails?: FinanceDocumentDetail[];
  user_organisation_name?: string;
  document_name?: any;
  remark_sub_category?: string;
}
export interface DPWCommisionDetails {
  misc_fees_commission: number;
  finance_commission: number;
  transaction_fees_below_$10000: number;
  transaction_fees_$10000_to_$50000: number;
  transaction_fees_above_$50000: number;
}

export interface ApplyFinCalcResults {
  discountRateType: any;
  dueDate: string;
  financeTenure: number;
  discountRate: number;
  discountFeeAmount: number;
  processingFee: number;
  processingFeeAmount: number;
  processingFeeType: string;
  financeRateSource?: string;
  financeRateType?: string;
  margin?: number;
  identifier: string;
  fees: Array<ApplicationFeeStructure>;
  factoringFee?: number;
  factoringFeeAmount?: number;
  factoringFeeType?: string;
}

export interface ApplicationFeeStructure {
  fee_name: string;
  fee_type: string;
  fee_value: string;
}
export interface TransactionCalcResults {
  discountFeeAmount: number;
  processingFeeAmount: number;
  discountRate: number;
  processingFee: number;
  financeCommissionAmt: number;
  transactionFeeAmount: number;
  miscFeeCommissionAmt: number;
  overdueFeeAmt: number;
  paybleAmount?: number;
  shortAmt?: number;
  extraAmt?: number;
  principal?: number;
  balances?: any;
  interestOnBalancePrincipal?: number;
  overdueInterestOnBalancePrincipal?: number;
  factoringFeeAmount?: number;
  pendingFactoringFeeTaxAmount?: number;
}

export interface ITrackingInfoRes {
  trackingInformation: ITrackingInformation[];
}

export interface ITrackingInformation {
  id: number;
  carrier_name: string;
  container_number?: string[];
  mbl_number?: string;
  first_port?: string;
  last_port?: string;
  booking_number: string;
  shipment_reference: string;
  container_created?: boolean;
  tracking_url: string;
  shipment_status: string[];
  finance_id: number;
  created_at: string;
  updated_at: string;
  deleted_at?: null;
  destinationAddress?: any;
  originAddress?: any;
  inco_term?: string;
  mblsDetails?: Array<any>;
  transport_modality?: string;
  hbl_number?: string;
}

export type TrackingInformationType = {
  mblNumber?: string;
  containerNumber?: string;
  carrierName?: string;
  bookingNumber?: string;
  shipmentReference?: string;
  hblNumber?: string;
}

export interface FeesActual {
  interest_rate_amount: number;
  transaction_fee_amount: number;
  tax_amount: number;
  overdue_interest_amount: number;
  other_charges_amount: number;
  actualFactoringFeeAmt?: number;
  actualFactoringFeeTaxAmt?: number;
}

export enum StepDisplayContent {
  FI_ASSESSMENT = 'The Financial Institution will assess the application and set its terms. The applicant will have to accept the terms thereafter for funds transfer.',
  FINANCIAL_TERMS_REVIEW = 'The Financial Institution has assessed the application and set the terms. The <PARTIES_INVOLVED> will have to accept the terms for funds transfer.'
}